@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.screens_shared

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.manager.LumaticaApiManager
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.LoginService
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.LumaticaCheckbox
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flex
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private const val BEREICH_EXTRA_STANDORTE = "infraStandorte"
private const val BEREICH_EXTRA_HALLEN = "infraHallen"

private val bereichJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun bereichApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private fun currentBereichUserId(): String =
    LoginService.getId()

private fun parseBereichSelection(raw: String): Set<String> =
    raw.split(",")
        .map { it.trim() }
        .filter { it.isNotBlank() }
        .toSet()

private fun encodeBereichSelection(values: Collection<String>): String =
    values.filter { it.isNotBlank() }.distinct().sorted().joinToString(",")

private fun standortLabel(entry: All): String =
    entry.standort.ifBlank { entry.firma.ifBlank { entry.id } }

private fun standortAdresse(entry: All): String =
    entry.adresse.ifBlank {
        listOf(
            listOf(entry.strasse, entry.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
            listOf(entry.plz, entry.ort).filter { it.isNotBlank() }.joinToString(" "),
            entry.land
        ).filter { it.isNotBlank() }.joinToString(" ")
    }

private fun standortCheckboxLabel(entry: All): String =
    listOf(standortLabel(entry), standortAdresse(entry))
        .filter { it.isNotBlank() }
        .joinToString(" - ")

private suspend fun loadBereiche(): List<All> {
    val userId = currentBereichUserId()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching { LumaticaApiManager.call("bereich/entries/${bereichApiPart(userId)}") }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { bereichJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private suspend fun loadStandorte(): List<All> {
    val userId = currentBereichUserId()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching { LumaticaApiManager.call("bereich/standorte/${bereichApiPart(userId)}") }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { bereichJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

private fun bereichSaveApi(bereich: All): String =
    listOf(
        "bereich",
        "save",
        currentBereichUserId(),
        bereich.id.ifBlank { "neu" },
        bereich.name,
        bereich.name.ifBlank { bereich.bezeichnung },
        bereich.infraStandorte,
        bereich.infraHallen
    ).joinToString("/") { bereichApiPart(it) }

private suspend fun saveBereich(bereich: All): All? {
    if (currentBereichUserId().isBlank()) return null
    val raw = runCatching { LumaticaApiManager.call(bereichSaveApi(bereich)) }.getOrDefault("")
    if (raw.isBlank() || raw == "null") return null
    return runCatching { bereichJson.decodeFromString(All.AllNames.serializer(), raw) }.getOrNull()
}

private suspend fun deleteBereich(id: String) {
    val userId = currentBereichUserId()
    if (userId.isBlank() || id.isBlank()) return
    LumaticaApiManager.call("bereich/delete/${bereichApiPart(userId)}/${bereichApiPart(id)}")
}

@Composable
fun SHAREDBereichsverwaltung(art: String = org.example.project.service.currentSaveArt()) {
    Style(cssRules = SharedWorkspaceStyle.cssRules)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    var bereiche by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedBereichId by remember { mutableStateOf("") }
    var draftBereich by remember { mutableStateOf<All?>(null) }
    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }

    suspend fun reload() {
        val loadedBereiche = loadBereiche()
            .sortedBy { it.name.ifBlank { it.bezeichnung.ifBlank { it.id } }.lowercase() }
        val loadedStandorte = loadStandorte()
            .sortedBy { standortLabel(it).lowercase() }

        bereiche = loadedBereiche
        standorte = loadedStandorte

        if (loadedBereiche.none { it.id == selectedBereichId }) {
            selectedBereichId = loadedBereiche.firstOrNull()?.id.orEmpty()
        }
        draftBereich = loadedBereiche.firstOrNull { it.id == selectedBereichId }
    }

    LaunchedEffect(art) {
        reload()
    }

    LaunchedEffect(selectedBereichId, bereiche) {
        draftBereich = bereiche.firstOrNull { it.id == selectedBereichId }
    }

    fun updateDraftSelections(standortIds: Set<String>) {
        val current = draftBereich ?: return
        draftBereich = current.copy(
            infraStandorte = encodeBereichSelection(standortIds),
            infraHallen = ""
        )
    }

    fun createBereich() {
        org.example.project.dashboard.AppDialogs.prompt("Bereichsprofilname:") { input ->
            val name = input?.trim().orEmpty()
            if (name.isBlank()) return@prompt
            val bereich = All(id = "neu", name = name, bezeichnung = name)
            scope.launch {
                val saved = saveBereich(bereich) ?: return@launch
                reload()
                selectedBereichId = saved.id
            }
        }
    }

    fun deleteBereich() {
        val id = selectedBereichId.takeIf { it.isNotBlank() } ?: return
        scope.launch {
            deleteBereich(id)
            reload()
        }
    }

    fun saveBereich() {
        val bereich = draftBereich ?: return
        scope.launch {
            val saved = saveBereich(bereich.copy(bezeichnung = bereich.name.ifBlank { bereich.bezeichnung })) ?: return@launch
            reload()
            selectedBereichId = saved.id
        }
    }

    val selectedStandorte = parseBereichSelection(draftBereich?.infraStandorte.orEmpty())

    Div({ classes(SharedWorkspaceStyle.workspace) }) {
        Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
            Div({
                classes(SharedWorkspaceStyle.contentInner)
                style {
                    maxWidth(100.percent)
                    flex(1)
                    minHeight(0.px)
                    overflowY("auto")
                }
            }) {
                if (internalTitleVisible) {
                    Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                        Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Bereichsverwaltung") }
                    }
                }

                Div({
                    classes(SharedWorkspaceStyle.actionBar)
                    style {
                        property("grid-template-columns", "220px repeat(3, minmax(0, 160px))")
                    }
                }) {
                    AppDropdown(
                        options = bereiche.map { All(key = it.id, label = it.name.ifBlank { it.bezeichnung.ifBlank { it.id } }) },
                        selectedValue = selectedBereichId.ifBlank { null },
                        onChange = { selectedBereichId = it },
                        placeholder = "Bereich wählen",
                        appearance = AppDropdownAppearance.Field,
                        attrs = { style { width(220.px); height(42.px) } }
                    )
                    LumaticaButton(
                        label = "Erstellen",
                        onClick = { createBereich() },
                        variant = LumaticaButtonVariant.Standard,
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Löschen",
                        onClick = { deleteBereich() },
                        variant = LumaticaButtonVariant.Standard,
                        enabled = selectedBereichId.isNotBlank(),
                        heightPx = 42
                    )
                    LumaticaButton(
                        label = "Speichern",
                        onClick = { saveBereich() },
                        variant = LumaticaButtonVariant.Action,
                        enabled = draftBereich != null,
                        heightPx = 42
                    )
                }

                Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                if (draftBereich == null) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            color(Color.white)
                            fontSize(14.px)
                            padding(40.px)
                        }
                    }) { Text("Kein Bereich ausgewählt.") }
                } else {
                    Div({ classes(SharedWorkspaceStyle.columnList) }) {
                        Div({ classes(SharedWorkspaceStyle.permissionColumn) }) {
                            Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Standorte") }
                            Div({
                                style {
                                    display(DisplayStyle.Flex)
                                    flexDirection(FlexDirection.Column)
                                    gap(10.px)
                                    marginTop(16.px)
                                }
                            }) {
                                standorte.forEach { standort ->
                                    val checked = standort.id in selectedStandorte
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            property("align-items", "center")
                                            gap(12.px)
                                        }
                                    }) {
                                        LumaticaCheckbox(
                                            checked = checked,
                                            onChange = { enabled ->
                                                val nextStandorte = selectedStandorte.toMutableSet()
                                                if (enabled) nextStandorte += standort.id else nextStandorte -= standort.id
                                                updateDraftSelections(nextStandorte)
                                            }
                                        )
                                        Span({
                                            classes(SharedWorkspaceStyle.permissionName)
                                            style {
                                                width(100.percent)
                                                property("white-space", "normal")
                                                property("overflow", "visible")
                                                property("text-overflow", "clip")
                                            }
                                        }) {
                                            Text(standortCheckboxLabel(standort))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
